import React, { useState } from "react";
import SalesOrderDetailsTable from "./SalesOrderDetailsTable";
import CurrencyFormat from "react-currency-format";
import { useNavigate } from "react-router-dom";
import { formatCustomDate } from "../../helpers/formatCustomDate";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import SalesOrderAddressModal from "./SalesOrderAddressModal";
import StatusBadge from "../../generals/StatusBadge";
import {
  User,
  Mail,
  Phone,
  Gift,
  Hash,
  Calendar,
  CreditCard,
  Link,
  MessageSquare,
  FileText,
  Package,
  Truck,
  MapPin,
  ExternalLink
} from "lucide-react";

function SalesOrderOverview(props) {
  const { salesData } = props;
  const navigate = useNavigate();
  const [addressModal, setAddressModal] = useState(false);

  const calculateSubTotal = (list) => {
    let result = 0;
    list?.forEach((element) => { result += element.total_amount; });
    return formatCustomNumber(result);
  };

  const calculateBaseTotal = (list) => {
    let result = 0;
    list?.forEach((element) => {
      result += element.item_sales_quantity * element.item_sales_rate;
    });
    const discountValue = salesData?.discount_amount;
    const discountType = salesData?.discount_type_id;
    if (discountValue && (discountType === 1 || discountType === 0)) result = result - discountValue;
    if (discountValue && discountType === 2) {
      let discount = (discountValue / 100) * result;
      result = Number(result) - Number(discount);
    }
    return formatCustomNumber(result);
  };

  function viewSubscription(subId) {
    if (!subId) return;
    navigate(`/${2}/${"Subscription"}/${"subscriptions"}/details/${subId}`);
  }

  const InfoRow = ({ icon: Icon, label, value, onClick }) => (
    <div className="flex items-start gap-2.5 py-1">
      {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold leading-tight">{label}</span>
        <span
          className={`text-sm font-medium text-gray-700 truncate ${onClick ? "text-blue-600 cursor-pointer hover:text-blue-800 transition-colors" : ""}`}
          onClick={onClick}
          title={value}
        >
          {value || <span className="text-gray-300">—</span>}
        </span>
      </div>
    </div>
  );

  const SectionHeader = ({ title }) => (
    <div className="px-4 py-2 border-b border-gray-100 bg-gray-50/50">
      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">{title}</h3>
    </div>
  );

  return (
    <section className="mt-4 w-full text-gray-700 space-y-4 pb-8">
      <SalesOrderAddressModal
        saleId={salesData?.sales_order_ref_no}
        isOpen={addressModal}
        defaultData={{
          country: salesData?.order_recipient_shipping_country_id,
          city: salesData?.order_recipient_shipping_city_id,
          area: salesData?.order_recipient_shipping_area_id,
          addressLine: salesData?.order_recipient_shipping_address_line_one,
        }}
        reload={props.reload}
        onClose={() => setAddressModal(false)}
      />

      {/* ── Header Card ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-blue-50 p-2.5 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-extrabold text-gray-900 tracking-tight">
                  {salesData?.sales_order_no}
                </h1>
                <StatusBadge
                  statusId={salesData?.sales_order_status_id}
                  statusName={salesData?.sales_order_status_name}
                />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Created: <span className="font-medium text-gray-700">{salesData?.created_date_string}</span>
                {salesData?.package_encoded_by && (
                  <span className="ml-2 pl-2 border-l border-gray-200">
                    by <span className="font-medium text-gray-700">{salesData?.package_encoded_by}</span>
                  </span>
                )}
              </p>
            </div>
          </div>

          {salesData?.sales_order_attachement_url?.length > 3 && (
            <a
              href={salesData?.sales_order_attachement_url}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-xs font-bold rounded-lg hover:bg-gray-800 transition shadow-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              VIEW ATTACHMENT
            </a>
          )}
        </div>
      </div>

      {/* ── Remark ── */}
      {salesData?.order_remark && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-300 border-l-[6px] border-l-amber-500 rounded-xl shadow-sm">
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 text-white shrink-0">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs uppercase tracking-wider text-amber-800 font-extrabold">Order Remark</span>
            <span className="mt-0.5 text-base font-semibold text-gray-900 whitespace-pre-line break-words">{salesData?.order_remark}</span>
          </div>
        </div>
      )}

      {/* ── Detail Grid ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-gray-100">

          {/* Col 1: Customer */}
          <div className="flex flex-col h-full">
            <SectionHeader title="Customer Info" />
            <div className="px-4 py-4 space-y-3 flex-1 bg-white">
              <InfoRow icon={User} label="Name" value={salesData?.customer_full_name} />
              <InfoRow icon={Mail} label="Email" value={salesData?.customer_email_address} />
              <InfoRow icon={Phone} label="Mobile" value={salesData?.customer_mobile_no} />
              <InfoRow icon={Gift} label="Gift Status" value={salesData?.gift_status_name} />

              {salesData?.gift_status_id === 1 && (
                <div className="mt-4 pt-4 border-t border-dashed border-gray-100">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">Recipient</p>
                  <InfoRow icon={User} label="Name" value={salesData?.recipient_full_name} />
                  <InfoRow icon={Phone} label="Mobile" value={salesData?.recipient_phone_number} />
                </div>
              )}
            </div>
          </div>

          {/* Col 2: Order Details */}
          <div className="flex flex-col h-full">
            <SectionHeader title="Order Details" />
            <div className="px-4 py-4 space-y-3 flex-1 bg-white">
              <InfoRow icon={Hash} label="Ref No" value={salesData?.sales_order_no} />
              <InfoRow icon={Calendar} label="Order Date" value={salesData?.sales_order_date_string} />
              <InfoRow icon={CreditCard} label="Payment Terms" value={salesData?.payment_term_name} />
              <InfoRow
                icon={Link}
                label="Subscription ID"
                value={salesData?.subscription_ref_no}
                onClick={salesData?.subscription_ref_no ? () => viewSubscription(salesData?.subscription_ref_no) : null}
              />
              {!salesData?.order_remark && (
                <InfoRow icon={MessageSquare} label="Remark" value={salesData?.order_remark} />
              )}
            </div>
          </div>

          {/* Col 3: Package & Shipment */}
          <div className="flex flex-col h-full">
            <SectionHeader title="Shipment" />
            <div className="px-4 py-4 space-y-3 flex-1 bg-white">
              <InfoRow icon={Package} label="Package No" value={salesData?.package_slip_number} />
              <InfoRow icon={Calendar} label="Package Date" value={formatCustomDate(salesData?.package_date)} />
              <InfoRow icon={Truck} label="Ship Date" value={formatCustomDate(salesData?.shipment_date)} />
              <InfoRow icon={Calendar} label="Delivery Date" value={formatCustomDate(salesData?.shipment_delivered_date)} />
              <InfoRow icon={User} label="Driver" value={salesData?.Delivery_User_Full_Name} />

            </div>
          </div>

          {/* Col 4: Addresses */}
          <div className="flex flex-col h-full">
            <SectionHeader title="Addresses" />
            <div className="px-4 py-4 space-y-5 flex-1 bg-white">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Shipping To</span>
                  </div>
                  {salesData?.sales_order_ref_no && (
                    <button
                      type="button"
                      onClick={() => setAddressModal(true)}
                      className="text-[10px] font-bold text-blue-600 hover:text-blue-800 transition uppercase tracking-tighter"
                    >
                      Edit Link
                    </button>
                  )}
                </div>
                <p className="text-sm font-medium text-gray-700 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-100">
                  {salesData?.order_recipient_full_shipping_address || <span className="text-gray-300 italic text-xs">No address specified</span>}
                </p>
              </div>

              {salesData?.gift_status_id === 1 && (
                <div className="pt-2">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Gift className="w-3.5 h-3.5 text-purple-500" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Gift Address</span>
                  </div>
                  <p className="text-sm font-medium text-gray-700 leading-relaxed bg-purple-50/50 p-3 rounded-lg border border-purple-100">
                    {[
                      salesData?.recipient_full_address_shipping,
                      salesData?.recipient_shipping_area_name,
                      salesData?.recipient_shipping_city_name,
                      salesData?.recipient_shipping_country_name,
                    ].filter(Boolean).join(", ") || <span className="text-gray-300 italic text-xs">No gift address</span>}
                  </p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── Item Table Section ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="px-6 py-4 bg-gray-50/80 border-b border-gray-100 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          <h2 className="text-sm font-bold text-gray-900">Line Items & Details</h2>
        </div>

        <div className="overflow-x-auto">
          <div className="min-h-[220px]">
            <SalesOrderDetailsTable salesData={salesData} tableData={salesData?.sales_order_details} />
          </div>
        </div>

        {/* Totals Section */}
        <div className="border-t border-gray-100 bg-gray-50/30 px-6 py-5 flex justify-end">
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm min-w-[280px] space-y-2.5">
            <div className="flex items-center justify-between text-xs text-gray-500 font-medium px-1">
              <span>Gross Amount</span>
              <CurrencyFormat
                value={calculateSubTotal(salesData?.sales_order_details)}
                displayType="text"
                thousandSeparator={true}
                prefix="₦"
                renderText={(v) => <span className="text-gray-700">{v}</span>}
              />
            </div>

            {salesData?.discount_amount > 0 && (
              <div className="flex items-center justify-between text-xs font-medium px-1">
                <span className="text-gray-500">Discount Applied</span>
                <CurrencyFormat
                  value={formatCustomNumber(salesData?.discount_amount)}
                  displayType="text"
                  thousandSeparator={true}
                  prefix={salesData?.discount_type_id === 2 ? "" : "₦"}
                  suffix={salesData?.discount_type_id === 2 ? "%" : ""}
                  renderText={(v) => <span className="text-rose-500">−{v}</span>}
                />
              </div>
            )}

            <div className="pt-2.5 mt-1 border-t border-gray-100 flex items-center justify-between px-1">
              <span className="text-sm font-extrabold text-gray-900 uppercase tracking-tight">Net Total</span>
              <CurrencyFormat
                value={calculateBaseTotal(salesData?.sales_order_details)}
                displayType="text"
                thousandSeparator={true}
                prefix="₦"
                renderText={(v) => <span className="text-xl font-black text-blue-600 tracking-tighter">{v}</span>}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SalesOrderOverview;